import { modelOptions, prop } from '@typegoose/typegoose';
import {
  DashboardType,
  CategoryGroup,
  ViewType,
  WidgetCategory,
  ContentOrientation,
  FeedbackType,
} from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { FilterConfig, StringConditions, IntConditions } from '../filters.model';
import { TimeRange } from '../time.model';

@modelOptions({ schemaOptions: { _id: false } })
class Size {
  @prop({ required: true })
  width: number;

  @prop({ required: true })
  height: number;
}

@modelOptions({ schemaOptions: { _id: false } })
class Position {
  @prop({ required: true })
  x: number;

  @prop({ required: true })
  y: number;
}

@modelOptions({ schemaOptions: { _id: false } })
class Layout {
  @prop({ required: true })
  position: Position;

  @prop({ required: true })
  size: Size;
}

@modelOptions({ schemaOptions: { _id: false } })
class WidgetConfig {
  @prop({ required: true })
  defaultSize: Size;

  @prop({ required: false })
  maxSize?: Size;

  @prop({ required: false })
  minSize?: Size;

  @prop({ required: true })
  resizable: boolean;

  @prop({ required: true })
  movable: boolean;

  @prop({ required: true })
  closable: boolean;

  @prop({ required: true })
  locked: boolean;

  @prop({ required: true, type: Number })
  resizeBreakingPoints: number[];

  @prop({ required: false })
  hideTitle?: boolean;

  @prop({ required: false })
  titleHasAction?: boolean;

  @prop({ required: false, type: String, enum: ContentOrientation })
  contentOrientation?: ContentOrientation;

  @prop({ required: false, type: String })
  widgetContentStyle?: string;
}

@modelOptions({ schemaOptions: { _id: false } })
class Widget {
  @prop({ required: true })
  id: string;

  @prop({ required: true, type: Number, enum: WidgetCategory })
  category: WidgetCategory;

  @prop({ required: true })
  config: WidgetConfig;
}

class DateConditions {
  @prop({ type: TimeRange, _id: false })
  public timeRange?: TimeRange;
}

@modelOptions({ schemaOptions: { _id: false } })
class Filter {
  @prop({ required: false, type: DateConditions, _id: false })
  dateTime?: DateConditions;

  @prop({ required: false, type: StringConditions, _id: false })
  company?: StringConditions;

  @prop({ required: false, type: StringConditions, _id: false })
  tags?: StringConditions;

  @prop({ required: false, type: IntConditions, _id: false })
  lastSeen?: IntConditions;
}

@modelOptions({ schemaOptions: { _id: false } })
class SmartViewWidget {
  @prop({ required: true })
  id: string;

  @prop({ required: true })
  defaultLayout: Layout;
}

@modelOptions({ schemaOptions: { _id: false } })
export class GenericViewConfig {
  @prop({
    required: true,
  })
  id: string;

  @prop({
    required: true,
  })
  label: string;

  @prop({ required: true, type: Number, enum: CategoryGroup })
  categoryGroup: CategoryGroup;

  @prop({ required: true, type: Number, enum: ViewType })
  type: ViewType;

  @prop({ required: true, type: Filter })
  filters: Array<Filter>;

  @prop({ required: true, type: SmartViewWidget })
  widgets: Array<SmartViewWidget>;
}

@modelOptions({ schemaOptions: { _id: false } })
class CanExport {
  @prop({ required: true })
  csv: boolean;

  @prop({ required: true })
  pdf: boolean;

  @prop({ required: true })
  report: boolean;
}

@modelOptions({ schemaOptions: { _id: false } })
class LiveView {
  @prop({ required: true })
  visible: boolean;

  @prop({ required: true })
  usable: boolean;
}

@modelOptions({ schemaOptions: { _id: false } })
class SettingsPanel {
  @prop({ required: true })
  visible: boolean;

  @prop({ required: true })
  hasSearch: boolean;

  @prop({ required: false })
  searchInputPlaceholder?: string;

  @prop({ required: false })
  searchInputNoResultsFoundText?: string;
}

@modelOptions({ schemaOptions: { _id: false } })
class SmartViewsPanel {
  @prop({ required: true })
  visible: boolean;

  @prop({ required: true })
  hasSearch: boolean;

  @prop({ required: true })
  title: string;

  @prop({ required: true })
  isOpen: boolean;

  @prop({ required: false })
  searchInputPlaceholder?: string;

  @prop({ required: false })
  searchInputNoResultsFoundText?: string;
}

@modelOptions({ schemaOptions: { _id: false } })
export class DashboardUiConfig {
  @prop({ required: true })
  canExport: CanExport;

  @prop({ required: true })
  liveView: LiveView;

  @prop({ required: true })
  settingsPanel: SettingsPanel;

  @prop({ required: true })
  smartViewsPanel: SmartViewsPanel;

  @prop({ required: false, type: Number, enum: FeedbackType })
  feedbackType?: FeedbackType;

  @prop({ required: false })
  resetView: boolean;
}

@modelOptions({ schemaOptions: { _id: false } })
class WidgetCategoryConfigs {
  @prop({ required: true })
  id: string;

  @prop({ required: true })
  title: string;

  @prop({ required: true })
  isOpen: boolean;

  @prop({ required: true, type: Number, enum: WidgetCategory })
  category: WidgetCategory;
}

@modelOptions({ schemaOptions: { _id: false } })
class SmartViewsCategoryConfigs {
  @prop({ required: true })
  id: string;

  @prop({ required: true })
  label: string;

  @prop({ required: true })
  isOpen: boolean;

  @prop({ required: true })
  collapsable: boolean;

  @prop({ required: true })
  emptyCategoryText: string;

  @prop({ required: true, type: Number, enum: CategoryGroup })
  group: CategoryGroup;
}

@modelOptions({
  schemaOptions: { collection: 'dashboardsTemplates', versionKey: false },
  options: { customName: 'dashboardsTemplates' },
})
export class DashboardTemplate {
  @prop({
    required: true,
    type: Number,
    enum: DashboardType,
  })
  dashboardType: DashboardType;

  @prop({ required: true, type: FilterConfig })
  filterConfigs: Array<FilterConfig>;

  @prop({
    required: true,
    type: GenericViewConfig,
  })
  smartViews: Array<GenericViewConfig>;

  @prop({ required: true, type: SmartViewsCategoryConfigs })
  smartViewsCategories: Array<SmartViewsCategoryConfigs>;

  @prop({ required: true, type: WidgetCategoryConfigs })
  widgetCategories: Array<WidgetCategoryConfigs>;

  @prop({ required: true })
  uiConfig: DashboardUiConfig;

  @prop({ required: true, type: Widget })
  availableWidgets: Array<Widget>;
}
